import { createTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'

import {
  accent,
  background,
  borderStrong,
  common,
  critical,
  divider,
  elevation,
  error,
  fontFamily,
  hardGrey,
  heroGradient,
  line,
  navLine,
  outlinedHover,
  primary,
  resolveScheme,
  rounded,
  secondary,
  softGrey,
  spacingBase,
  success,
  text,
  warning,
} from './tokens'

declare module '@mui/material/styles' {
  interface Palette {
    /** The brand's bright green, used for emphasis rather than for fills. */
    accent: PaletteColor
    /** Neutral surfaces and low-emphasis borders. */
    softGrey: PaletteColor
    /** High-contrast neutrals for text-adjacent chrome. */
    hardGrey: PaletteColor
    /** Severity between `warning` and `error`. */
    critical: PaletteColor
    /** A border that must stand out from the page. */
    borderStrong: string
    /** The outline of a box, card or panel. */
    line: string
    /** Shadows, which differ per scheme. */
    elevation: { card: string; cardHover: string; menu: string }
    /** The backdrop behind a hero area, such as sign-in. */
    heroGradient: string
    /** The gradient bar under the app header. */
    navLine: string
    /** The fill of an outlined primary button: the Skillable primary's contrast color, kept when a site has its own. */
    outlinedFill: string
    /** An outlined primary button's hover fill and the label and border on it. */
    outlinedHover: { fill: string; text: string }
  }

  interface PaletteOptions {
    accent?: SimplePaletteColorOptions
    softGrey?: SimplePaletteColorOptions
    hardGrey?: SimplePaletteColorOptions
    critical?: SimplePaletteColorOptions
    borderStrong?: string
    line?: string
    elevation?: { card: string; cardHover: string; menu: string }
    heroGradient?: string
    navLine?: string
    outlinedFill?: string
    outlinedHover?: { fill: string; text: string }
  }

  /** The archetype gives each severity a tinted background as well as a solid tone. */
  interface PaletteColor {
    soft?: string
    /** `softGrey` only. */
    translucent?: string
    /** Text and icons in the severity's tone; `error`, `warning` and `critical`. */
    textOnly?: string
    /** `primary` only. */
    ring?: string
    /** `primary` only. */
    glow?: string
  }

  interface TypeBackground {
    /** Slight variance from the page. */
    contrastXs: string
    /** Neutral fill one step up from `paper`. */
    contrastSm: string
    /** Neutral fill two steps up from `paper`. */
    contrastMd: string
    /** The fill behind any disabled control. */
    disabled: string
  }

  interface SimplePaletteColorOptions {
    soft?: string
    translucent?: string
    textOnly?: string
    ring?: string
    glow?: string
  }
}

/**
 * Builds the palette for one color scheme.
 *
 * @param scheme - The scheme to resolve every token pair against.
 * @returns Palette options for that scheme.
 */
function paletteFor(scheme: 'light' | 'dark') {
  return {
    primary: resolveScheme(primary, scheme),
    secondary: resolveScheme(secondary, scheme),
    accent: resolveScheme(accent, scheme),
    softGrey: resolveScheme(softGrey, scheme),
    hardGrey: resolveScheme(hardGrey, scheme),
    error: resolveScheme(error, scheme),
    critical: resolveScheme(critical, scheme),
    warning: resolveScheme(warning, scheme),
    success: resolveScheme(success, scheme),
    background: resolveScheme(background, scheme),
    text: resolveScheme(text, scheme),
    common: resolveScheme(common, scheme),
    divider: divider[scheme],
    borderStrong: borderStrong[scheme],
    line: line[scheme],
    elevation: resolveScheme(elevation, scheme),
    heroGradient: heroGradient[scheme],
    navLine: navLine[scheme],
    outlinedFill: primary.contrastText[scheme],
    outlinedHover: resolveScheme(outlinedHover, scheme),
  }
}

/**
 * The application theme.
 *
 * `cssVariables` is on so every token is emitted as a CSS custom property under the `--tms-`
 * prefix. That is what lets Tailwind read the same values as MUI (see the `@theme inline`
 * block in `index.css`) instead of the two systems keeping separate copies of the palette.
 *
 * The color scheme is selected by `data-theme` on `<html>`, which is also what
 * `tailwind.config.ts` watches — one attribute drives both.
 */
export const theme: Theme = createTheme({
  cssVariables: {
    cssVarPrefix: 'tms',
    colorSchemeSelector: '[data-theme="%s"]',
  },
  defaultColorScheme: 'light',
  colorSchemes: {
    light: { palette: paletteFor('light') },
    dark: { palette: paletteFor('dark') },
  },
  spacing: spacingBase,
  shape: {
    borderRadius: rounded.default,
  },
  // sm..xl match MUI's defaults, which is what the archetype specifies. The archetype's
  // `xs: 444px` is a Figma artboard width, not a breakpoint: MUI requires its first
  // breakpoint to be 0 so that `up('xs')` means "always".
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  typography: {
    fontFamily: fontFamily.body,
    h1: { fontFamily: fontFamily.heading, fontSize: '6rem', fontWeight: 500, lineHeight: 1.2 },
    h2: { fontFamily: fontFamily.heading, fontSize: '3.75rem', fontWeight: 500, lineHeight: 1.2 },
    h3: { fontFamily: fontFamily.heading, fontSize: '3rem', fontWeight: 500, lineHeight: 1.2 },
    h4: { fontFamily: fontFamily.heading, fontSize: '2rem', fontWeight: 500, lineHeight: 1.2 },
    h5: { fontFamily: fontFamily.heading, fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.2 },
    h6: { fontFamily: fontFamily.heading, fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.2 },
    subtitle1: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.4 },
    subtitle2: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.4 },
    body1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4 },
    body2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
    overline: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66 },
    button: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { colorScheme: 'light dark' },
        body: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        // Tokens MUI does not emit itself, for the `@theme inline` block in `index.css`.
        ':root': {
          '--tms-font-heading': fontFamily.heading,
          '--tms-radius-xl': `${rounded.xl}px`,
        },
      },
    },
    // The mockups' buttons have no ripple.
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ theme: t }) => ({
          alignItems: 'center',
          borderRadius: rounded.default,
          // Never squeezed below its label in a flex row: MUI's 64px `min-width` would otherwise
          // let it shrink until the padding is gone and the label is pushed to one edge.
          flexShrink: 0,
          // design.md's `iconGap`, between an icon and the label however the icon is passed.
          gap: t.spacing(2),
          // Line height 1 and block icons keep a child icon on the label's visual center.
          lineHeight: 1,
          '& svg, & .material-symbols-outlined': { display: 'block' },
          '&.Mui-focusVisible': {
            borderRadius: rounded.focus,
            outline: `2px solid ${t.vars.palette.primary.main}`,
            outlineOffset: 2,
          },
        }),
        // MUI's own icon margins would add to the gap above and pull the icon into the padding.
        startIcon: { margin: 0 },
        endIcon: { margin: 0 },
        sizeSmall: { fontSize: '0.875rem', minHeight: 33, padding: '8px 16px' },
        sizeMedium: { fontSize: '1rem', minHeight: 36, padding: '6px 16px' },
        sizeLarge: { fontSize: '1.125rem', minHeight: 36, padding: '8px 22px' },
      },
      variants: [
        {
          props: { variant: 'contained' },
          style: ({ theme: t }) => ({
            '&.Mui-disabled': {
              backgroundColor: t.vars.palette.background.disabled,
              color: t.vars.palette.text.disabled,
            },
          }),
        },
        {
          // The mockups define outlined for primary only: neutral border and label, on the
          // primary contrast fill (`outlinedFill`, which a site's primary color leaves alone).
          props: { variant: 'outlined', color: 'primary' },
          style: ({ theme: t }) => ({
            backgroundColor: t.vars.palette.outlinedFill,
            borderColor: t.vars.palette.text.primary,
            color: t.vars.palette.text.primary,
            // Not while busy: a busy button keeps pointer events, and its dimmed label would not read on the hover fill.
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: t.vars.palette.outlinedHover.fill,
              borderColor: t.vars.palette.outlinedHover.text,
              color: t.vars.palette.outlinedHover.text,
            },
            // As the mockup: the surface stays, the border and label dim.
            '&.Mui-disabled': {
              borderColor: t.vars.palette.background.disabled,
              color: t.vars.palette.text.disabled,
            },
          }),
        },
        {
          // The mockups hover on `contrastMd`; `contrastSm` keeps every label at 4.5:1 there.
          props: { variant: 'text' },
          style: ({ theme: t }) => ({
            '&:hover': { backgroundColor: t.vars.palette.background.contrastSm },
            '&.Mui-disabled': { color: t.vars.palette.text.disabled },
          }),
        },
        // design.md's `button-text-*` padding: a text button hugs its label.
        { props: { variant: 'text', size: 'small' }, style: { padding: '4px 5px' } },
        { props: { variant: 'text', size: 'medium' }, style: { padding: '6px 8px' } },
        { props: { variant: 'text', size: 'large' }, style: { padding: '8px 11px' } },
        {
          // Warning's main tone is too light for a label; its text tone reads.
          props: { variant: 'text', color: 'warning' },
          style: ({ theme: t }) => ({ color: t.vars.palette.warning.textOnly }),
        },
        {
          props: { variant: 'text', color: 'inherit' },
          style: ({ theme: t }) => ({ color: t.vars.palette.text.primary }),
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 9999,
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1,
          height: 29,
          '&:focus-visible, &.Mui-focusVisible': {
            outline: `2px solid ${t.vars.palette.primary.main}`,
            outlineOffset: 2,
          },
        }),
        label: { paddingLeft: 16, paddingRight: 16, paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
        // An outlined pill with a strong border when off, a filled primary pill when on.
        // Keyboard focus keeps the hover fill, not MUI's darker one, which would sink the
        // label's contrast.
        colorDefault: ({ theme: t }) => ({
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: t.vars.palette.borderStrong,
          color: t.vars.palette.text.primary,
          '&:hover, &.Mui-focusVisible': { backgroundColor: t.vars.palette.background.contrastSm },
        }),
        colorPrimary: ({ theme: t }) => ({
          backgroundColor: t.vars.palette.primary.main,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: t.vars.palette.primary.main,
          color: t.vars.palette.primary.contrastText,
          '&:hover, &.Mui-focusVisible': { backgroundColor: t.vars.palette.primary.dark },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.vars.palette.text.disabled },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.vars.palette.text.secondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.vars.palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: t.vars.palette.error.main,
          },
          '&.Mui-disabled': { opacity: 0.6 },
          // A value the viewer may not change: filled like a locked TextField, with no arrow or hover.
          '&.MuiInputBase-readOnly': {
            backgroundColor: t.vars.palette.background.contrastSm,
            '& .MuiSelect-select': { cursor: 'default' },
            '& .MuiSelect-icon': { display: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.vars.palette.text.disabled },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        // A long value wraps rather than running past a narrow screen's edge.
        select: ({ theme: t }) => ({ color: t.vars.palette.text.secondary, fontSize: '0.875rem', whiteSpace: 'normal' }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        // Paper gives the surface and hairline; a menu adds the mockups' paper shadow.
        paper: ({ theme: t }) => ({ boxShadow: t.vars.palette.elevation.menu }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          fontSize: '0.875rem',
          '&:not(:last-of-type)': { borderBottom: `1px solid ${t.vars.palette.divider}` },
          // Keyboard focus takes the hover fill too: MUI's darker focus fills sink the primary
          // label of a selected item below 4.5:1.
          '&:hover, &.Mui-focusVisible': { backgroundColor: t.vars.palette.background.contrastSm },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: t.vars.palette.primary.main,
            fontWeight: 600,
          },
          '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: t.vars.palette.background.contrastSm,
          },
        }),
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundImage: 'none',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: t.vars.palette.divider,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: rounded.default },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: rounded.default },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
    },
  },
})
